import React, { useState } from 'react';
import classnames from 'classnames';
import { Box, Button, Accordion, Typography } from '@lucid/lego';
import type {
  OrderNotification,
  NotificationVehicle,
} from '../../../../api/services/lupp/order/importer/dto.importer';
import { AccordionHeader } from './AccordionHeader/AccordionHeader';
import RejectNotificationModal from './RejectNotificationModal/RejectNotificationModal';
import ComparisonModal from './ComparisonModal/ComparisonModal';
import styles from './Notifications.module.scss';

export type NotificationsProps = {
  /** Notification items returned by GET /orders/notifications */
  notifications: OrderNotification[];
  /** Called when Accept is chosen in the comparison modal for a WAITING_FOR_CONFIRMATION vehicle item */
  onAcceptItem?: (vehicleOrderNumber: string) => void;
  /** Called when Accept All is clicked in the accordion header of a WAITING_FOR_CONFIRMATION notification */
  onAcceptAll?: (notification: OrderNotification) => void;
  /** Called when Reject is chosen and confirmed with a reason for a WAITING_FOR_CONFIRMATION vehicle item */
  onRejectItem?: (vehicleOrderNumber: string, reason: string) => void;
  /** Whether the reject API call is in flight */
  isRejectPending?: boolean;
  /** Called when a vehicle order number is clicked — caller handles navigation */
  onItemClick?: (vehicleOrderNumber: string) => void;
  /** Called when Go to Delivery is clicked on a READY_FOR_DISPATCH vehicle item */
  onGoToDelivery?: (vehicleOrderNumber: string) => void;
  theme: 'light' | 'dark';
  dataTestId: string;
  labels?: Record<string, string>;
};

/**
 * Renders order notification banners for the Order Desk page.
 * Each notification appears as a beige accordion with a title, description, and CTA buttons.
 * Returns null when there are no notifications.
 * @component
 * @param props.notifications - Notification items from GET /orders/notifications
 * @param props.theme - Theme for the component
 * @param props.dataTestId - Test ID for the root element
 * @returns {JSX.Element | null}
 */
const Notifications: React.FC<NotificationsProps> = ({
  notifications,
  onAcceptItem,
  onAcceptAll,
  onRejectItem,
  onItemClick,
  onGoToDelivery,
  isRejectPending = false,
  theme,
  dataTestId,
  labels,
}) => {
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const [comparisonTarget, setComparisonTarget] = useState<NotificationVehicle | null>(null);

  if (!notifications.length) return null;

  const invertedTheme = theme === 'light' ? 'dark' : 'light';

  return (
    <>
      <Box className={styles.root} data-testid={dataTestId}>
        {notifications.map((notification, index) => {
          const accordionTitle = (
            <AccordionHeader
              theme={theme}
              iconName="info"
              iconCategory="global"
              title={
                notification.type === 'WAITING_FOR_CONFIRMATION'
                  ? (
                      labels?.['notifications.waitingForConfirmationTitle'] ?? notification.title
                    ).replace('{{count}}', String(notification.vehicles.length))
                  : notification.type === 'READY_FOR_DISPATCH'
                    ? (
                        labels?.['notifications.readyForDispatchTitle'] ?? notification.title
                      ).replace('{{count}}', String(notification.vehicles.length))
                    : notification.title
              }
              subtitle={
                notification.type === 'WAITING_FOR_CONFIRMATION'
                  ? (labels?.['notifications.waitingForConfirmationSubtitle'] ??
                    notification.description)
                  : notification.type === 'READY_FOR_DISPATCH'
                    ? (labels?.['notifications.readyForDispatchSubtitle'] ??
                      notification.description)
                    : notification.description
              }
              actionButton={
                notification.type === 'WAITING_FOR_CONFIRMATION' && onAcceptAll ? (
                  <Button
                    theme={invertedTheme}
                    type="primary"
                    color="neutral"
                    label={labels?.['notifications.acceptAsadll'] ?? 'Accept All'}
                    dataTestId={`${dataTestId}-${index}-accept-all`}
                    gaComponent="order-notifications"
                    handleClick={(e) => {
                      e.stopPropagation();
                      onAcceptAll(notification);
                    }}
                    size="small"
                  />
                ) : undefined
              }
            />
          );

          return (
            <Accordion
              key={`${notification.type}-${index}`}
              title={accordionTitle}
              theme={theme}
              variant="beige"
              dataTestId={`${dataTestId}-${index}-accordion`}
            >
              <Box className={styles.vehicleList}>
                {notification.vehicles.map((vehicle) => {
                  const isComparable =
                    notification.type === 'WAITING_FOR_CONFIRMATION' &&
                    !!vehicle.orderNumber &&
                    !!vehicle.orderlineItemNumber;

                  return (
                    <Box
                      key={`${vehicle.orderNumber}-${vehicle.orderlineItemNumber}-${vehicle.vin}`}
                      className={classnames(styles.vehicleItem, {
                        [styles.clickable]: isComparable,
                      })}
                      onClick={isComparable ? () => setComparisonTarget(vehicle) : undefined}
                    >
                      {vehicle.orderlineItemNumber && (
                        <Typography
                          variant="body"
                          size="small"
                          color={onItemClick ? 'link' : 'black'}
                          theme={theme}
                          onClick={
                            onItemClick
                              ? (e) => {
                                  e.stopPropagation();
                                  onItemClick(vehicle.orderlineItemNumber!);
                                }
                              : undefined
                          }
                          className={styles.deliveryOrderNumber}
                        >
                          {vehicle.orderlineItemNumber}
                        </Typography>
                      )}
                      {vehicle.deliveryNotes && (
                        <Typography
                          variant="body"
                          size="small"
                          color="black"
                          theme={theme}
                          className={styles.deliveryNotes}
                        >
                          {vehicle.deliveryNotes}
                        </Typography>
                      )}
                      {notification.type === 'WAITING_FOR_CONFIRMATION' &&
                        (onAcceptItem || onRejectItem) && (
                          <Button
                            theme={invertedTheme}
                            type="primary"
                            color="neutral"
                            label={labels?.['notifications.review'] ?? 'Review'}
                            dataTestId={`${dataTestId}-${index}-${vehicle.orderNumber}-review`}
                            gaComponent="order-notifications"
                            handleClick={(e) => {
                              e.stopPropagation();
                              setComparisonTarget(vehicle);
                            }}
                            size="small"
                          />
                        )}
                      {notification.type === 'READY_FOR_DISPATCH' && onGoToDelivery && (
                        <Button
                          theme={invertedTheme}
                          type="primary"
                          color="neutral"
                          label={labels?.['notifications.goToDelivery'] ?? 'Go to Delivery'}
                          dataTestId={`${dataTestId}-${index}-${vehicle.orderNumber}-go-to-delivery`}
                          gaComponent="order-notifications"
                          handleClick={() => onGoToDelivery(vehicle.orderNumber)}
                          size="small"
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Accordion>
          );
        })}
      </Box>
      <RejectNotificationModal
        dataTestId="reject-notification-modal"
        isOpen={rejectTarget !== null}
        onClose={() => setRejectTarget(null)}
        onSubmit={(reason) => {
          if (rejectTarget) {
            onRejectItem?.(rejectTarget, reason);
            setRejectTarget(null);
          }
        }}
        isPending={isRejectPending}
        theme="light"
        labels={labels}
      />
      <ComparisonModal
        dataTestId="vehicle-comparison-modal"
        isOpen={comparisonTarget !== null}
        onClose={() => setComparisonTarget(null)}
        orderNumber={comparisonTarget?.orderNumber ?? null}
        orderLineNumber={comparisonTarget?.orderlineItemNumber ?? null}
        onAccept={() => {
          if (comparisonTarget) {
            onAcceptItem?.(comparisonTarget.orderNumber);
            setComparisonTarget(null);
          }
        }}
        onReject={() => {
          if (comparisonTarget) {
            setRejectTarget(comparisonTarget.orderNumber);
            setComparisonTarget(null);
          }
        }}
        isActionPending={isRejectPending}
        theme="light"
        labels={labels}
      />
    </>
  );
};

export default Notifications;
